import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  DropZone,
  InlineStack,
  Text,
  BlockStack,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "features/datepicker/date-picker";
import {
  createTripExpense,
  updateTripExpense,
  uploadExpenseReceipt,
} from "services/business-trip.service";
import { AppDispatch } from "configureStore";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchBusinessTripExpenses } from "containers/business-trip-view-container/business-trip-container.slice";
import { BusinessTripExpense, ExpenseCategory } from "types/BusinessTrip";
import {
  EXPENSE_CATEGORY_OPTIONS,
  EXPENSE_CURRENCY_OPTIONS,
} from "./constants";

interface BusinessTripExpenseFormProps {
  tripId: string;
  expense?: BusinessTripExpense | null;
}

const BusinessTripExpenseForm: React.FC<BusinessTripExpenseFormProps> = ({
  tripId,
  expense,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const isEditMode = !!expense?.id;

  const [category, setCategory] = useState<string>(expense?.category || "");
  const [description, setDescription] = useState(expense?.description || "");
  const [date, setDate] = useState(expense?.date?.split("T")[0] || "");
  const [amount, setAmount] = useState(
    expense?.amount != null ? String(expense.amount) : ""
  );
  const [currency, setCurrency] = useState(expense?.currency || "EUR");
  const [exchangeRate, setExchangeRate] = useState(
    expense?.exchangeRate != null ? String(expense.exchangeRate) : "1"
  );
  const [receiptFileId, setReceiptFileId] = useState<string | null>(
    expense?.receiptFileId || null
  );
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCategory(expense?.category || "");
    setDescription(expense?.description || "");
    setDate(expense?.date?.split("T")[0] || "");
    setAmount(expense?.amount != null ? String(expense.amount) : "");
    setCurrency(expense?.currency || "EUR");
    setExchangeRate(
      expense?.exchangeRate != null ? String(expense.exchangeRate) : "1"
    );
    setReceiptFileId(expense?.receiptFileId || null);
    setReceiptFile(null);
    setErrors({});
    setErrMsg(undefined);
    setSuccessMessage(undefined);
  }, [expense]);

  const showExchangeRate = currency !== "EUR";

  const computedAmountEur = useMemo(() => {
    const amt = parseFloat(amount);
    const rate = parseFloat(exchangeRate);
    if (isNaN(amt) || isNaN(rate) || rate <= 0) return "";
    if (currency === "EUR") return amt.toFixed(2);
    return (amt / rate).toFixed(2);
  }, [amount, exchangeRate, currency]);

  const handleFileDrop = (_files: File[], acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setReceiptFile(acceptedFiles[0]);
    }
  };

  const handleFormSubmit = async () => {
    setErrors({});
    setErrMsg(undefined);
    setSuccessMessage(undefined);
    if (!companyId) return;

    const newErrors: Record<string, string> = {};
    if (!category) {
      newErrors.category =
        t("field_is_required") || "Laukelis yra privalomas";
    }
    if (!date) {
      newErrors.date = t("field_is_required") || "Laukelis yra privalomas";
    }
    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      newErrors.amount = t("invalid_amount") || "Netinkama suma";
    }
    if (showExchangeRate) {
      const parsedRate = parseFloat(exchangeRate);
      if (!exchangeRate || isNaN(parsedRate) || parsedRate <= 0) {
        newErrors.exchangeRate =
          t("invalid_exchange_rate") || "Netinkamas kursas";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);

      let finalReceiptFileId = receiptFileId;
      if (receiptFile && companyId) {
        const uploaded = await uploadExpenseReceipt(
          companyId,
          tripId,
          receiptFile
        );
        finalReceiptFileId = uploaded.fileId;
      }

      const payload: Partial<BusinessTripExpense> = {
        category: category as ExpenseCategory,
        description: description || undefined,
        date,
        amount: parsedAmount,
        currency,
        exchangeRate: showExchangeRate ? parseFloat(exchangeRate) : 1,
        receiptFileId: finalReceiptFileId || undefined,
      };

      if (isEditMode && expense?.id) {
        await updateTripExpense(companyId, tripId, expense.id, payload);
      } else {
        await createTripExpense(companyId, tripId, payload);
      }

      setSuccessMessage("ok");
      dispatch(fetchBusinessTripExpenses({ companyId, tripId }));
      setTimeout(() => {
        dispatch(setIsModalxOpen(false));
      }, 1000);
    } catch (e: any) {
      const serverMsg = e?.response?.data?.error;
      setErrMsg(serverMsg || "error");
    } finally {
      setIsLoading(false);
    }
  };

  const categoryOptions = [
    {
      value: "",
      label: t("files_template_custom_type_select_select_an_option"),
    },
    ...EXPENSE_CATEGORY_OPTIONS.map((option) => ({
      value: option.value,
      label: t(option.label),
    })),
  ];

  return (
    <Form onSubmit={handleFormSubmit}>
      {(errMsg || successMessage) && (
        <div style={{ marginBottom: "16px" }}>
          {errMsg && <Banner tone="critical">{t(errMsg)}</Banner>}
          {successMessage && (
            <Banner tone="success">{t(successMessage)}</Banner>
          )}
        </div>
      )}
      <FormLayout>
        <Select
          label={t("expense_category")}
          options={categoryOptions}
          value={category}
          onChange={setCategory}
          error={errors.category}
          requiredIndicator
        />
        <TextField
          label={t("description")}
          value={description}
          onChange={setDescription}
          autoComplete="off"
          maxLength={500}
          showCharacterCount
        />
        <DatePicker
          title={t("date")}
          selected={date}
          onChange={(value) => {
            if (!value) setDate("");
            else if (value instanceof Date)
              setDate(value.toISOString().split("T")[0]);
            else setDate(String(value));
          }}
          isRequired={true}
          disableDatesAfter=""
          fullWidth
        />
        <FormLayout.Group>
          <TextField
            label={t("amount")}
            value={amount}
            onChange={setAmount}
            type="number"
            autoComplete="off"
            error={errors.amount}
            requiredIndicator
          />
          <Select
            label={t("currency")}
            options={EXPENSE_CURRENCY_OPTIONS}
            value={currency}
            onChange={setCurrency}
          />
        </FormLayout.Group>
        {showExchangeRate && (
          <>
            <TextField
              label={t("exchange_rate")}
              value={exchangeRate}
              onChange={setExchangeRate}
              type="number"
              autoComplete="off"
              error={errors.exchangeRate}
              requiredIndicator
              helpText={
                <a
                  href="https://www.lb.lt/lt/kasdien-skelbiami-euro-ir-uzsienio-valiutu-santykiai-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("exchange_rate_reference_link")}
                </a>
              }
            />
            {computedAmountEur && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("amount_in_eur")}: {computedAmountEur} EUR
              </Text>
            )}
          </>
        )}
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {t("receipt")}
          </Text>
          {receiptFileId && !receiptFile && (
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodySm" tone="subdued">
                {t("receipt_attached")}
              </Text>
            </InlineStack>
          )}
          <DropZone
            onDrop={handleFileDrop}
            allowMultiple={false}
            accept="image/jpeg,image/png,application/pdf"
            variableHeight
          >
            {receiptFile ? (
              <div style={{ padding: "12px" }}>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" variant="bodySm">
                    {receiptFile.name}
                  </Text>
                </InlineStack>
              </div>
            ) : (
              <DropZone.FileUpload
                actionHint={t("receipt_upload_hint")}
              />
            )}
          </DropZone>
        </BlockStack>
        <Button submit loading={isLoading} variant="primary">
          {t(isEditMode ? "update" : "save")}
        </Button>
      </FormLayout>
    </Form>
  );
};

export default BusinessTripExpenseForm;
